import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  FileText,
  Download,
  Trash2,
  Sparkles,
  Loader2,
  Lock,
  Ticket,
  Crown,
  Calendar,
  MapPin,
  Briefcase,
  FileDown,
} from "lucide-react";
import { API } from "@/lib/constants";

const LABELS = {
  en: {
    title: "My CVs",
    sub: "All your generated CVs — download anytime.",
    back: "Back to home",
    empty_title: "No CVs yet",
    empty_sub: "Optimize your first CV and it will appear here.",
    empty_cta: "Optimize my CV",
    locked_title: "Premium feature",
    locked_sub: "CV history is a Premium perk. Redeem a promo code or upgrade to unlock unlimited history.",
    locked_cta: "Redeem promo code",
    download_cv: "CV (PDF)",
    download_docx: "CV (Word)",
    download_cover: "Cover letter",
    download_action: "Action plan",
    delete_confirm: "Delete this CV from your history?",
    for_job: "For",
    ats: "ATS score",
    loading: "Loading your history…",
  },
  fr: {
    title: "Mes CV",
    sub: "Tous tes CV générés — téléchargeables à tout moment.",
    back: "Retour à l'accueil",
    empty_title: "Pas encore de CV",
    empty_sub: "Optimise ton premier CV et il apparaîtra ici.",
    empty_cta: "Optimiser mon CV",
    locked_title: "Fonctionnalité Premium",
    locked_sub: "L'historique des CV est réservé aux Premium. Utilise un code promo ou passe en Premium pour un historique illimité.",
    locked_cta: "Utiliser un code promo",
    download_cv: "CV (PDF)",
    download_docx: "CV (Word)",
    download_cover: "Lettre de motiv.",
    download_action: "Plan d'action",
    delete_confirm: "Supprimer ce CV de ton historique ?",
    for_job: "Pour",
    ats: "Score ATS",
    loading: "Chargement de ton historique…",
  },
  de: {
    title: "Meine Lebensläufe",
    sub: "Alle deine generierten CVs — jederzeit herunterladbar.",
    back: "Zurück zur Startseite",
    empty_title: "Noch keine CVs",
    empty_sub: "Optimiere deinen ersten CV und er erscheint hier.",
    empty_cta: "Meinen CV optimieren",
    locked_title: "Premium-Funktion",
    locked_sub: "Der CV-Verlauf ist Premium-Nutzern vorbehalten. Löse einen Promo-Code ein oder werde Premium.",
    locked_cta: "Promo-Code einlösen",
    download_cv: "CV (PDF)",
    download_docx: "CV (Word)",
    download_cover: "Anschreiben",
    download_action: "Aktionsplan",
    delete_confirm: "Diesen CV aus dem Verlauf löschen?",
    for_job: "Für",
    ats: "ATS-Punktzahl",
    loading: "Verlauf wird geladen…",
  },
  it: {
    title: "I miei CV",
    sub: "Tutti i tuoi CV generati — scaricabili in ogni momento.",
    back: "Torna alla home",
    empty_title: "Ancora nessun CV",
    empty_sub: "Ottimizza il tuo primo CV e apparirà qui.",
    empty_cta: "Ottimizza il mio CV",
    locked_title: "Funzione Premium",
    locked_sub: "La cronologia dei CV è riservata ai Premium. Riscatta un codice promo per sbloccare.",
    locked_cta: "Riscatta codice promo",
    download_cv: "CV (PDF)",
    download_docx: "CV (Word)",
    download_cover: "Lettera di motiv.",
    download_action: "Piano d'azione",
    delete_confirm: "Eliminare questo CV dalla cronologia?",
    for_job: "Per",
    ats: "Punteggio ATS",
    loading: "Caricamento cronologia…",
  },
  es: {
    title: "Mis CV",
    sub: "Todos tus CV generados — descargables en cualquier momento.",
    back: "Volver al inicio",
    empty_title: "Aún no hay CV",
    empty_sub: "Optimiza tu primer CV y aparecerá aquí.",
    empty_cta: "Optimizar mi CV",
    locked_title: "Función Premium",
    locked_sub: "El historial de CV es una ventaja Premium. Canjea un código promo para desbloquear.",
    locked_cta: "Canjear código promo",
    download_cv: "CV (PDF)",
    download_docx: "CV (Word)",
    download_cover: "Carta de present.",
    download_action: "Plan de acción",
    delete_confirm: "¿Eliminar este CV de tu historial?",
    for_job: "Para",
    ats: "Puntuación ATS",
    loading: "Cargando historial…",
  },
  pt: {
    title: "Os meus CVs",
    sub: "Todos os teus CVs gerados — descarregáveis a qualquer momento.",
    back: "Voltar ao início",
    empty_title: "Ainda sem CVs",
    empty_sub: "Otimiza o teu primeiro CV e ele aparecerá aqui.",
    empty_cta: "Otimizar o meu CV",
    locked_title: "Função Premium",
    locked_sub: "O histórico de CV é uma vantagem Premium. Resgata um código promo para desbloquear.",
    locked_cta: "Resgatar código promo",
    download_cv: "CV (PDF)",
    download_docx: "CV (Word)",
    download_cover: "Carta de motiv.",
    download_action: "Plano de ação",
    delete_confirm: "Eliminar este CV do teu histórico?",
    for_job: "Para",
    ats: "Pontuação ATS",
    loading: "A carregar histórico…",
  },
};

const FILE_LABELS_KEY = {
  cv_pdf: "download_cv",
  cv_docx: "download_docx",
  cover_pdf: "download_cover",
  action_plan_pdf: "download_action",
};

const FILE_ICONS = {
  cv_pdf: FileText,
  cv_docx: FileText,
  cover_pdf: FileDown,
  action_plan_pdf: FileDown,
};

const formatDate = (iso, lang) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "en" ? "en-AU" : lang, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export function MyCVsPage({ lang = "en", onBack, onStartWizard, onOpenRedeem }) {
  const t = LABELS[lang] || LABELS.en;
  const [state, setState] = useState({ loading: true, locked: false, items: [] });
  const [error, setError] = useState("");

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await axios.get(`${API}/user/cvs`, { withCredentials: true });
      setState({ loading: false, locked: !!r.data.locked, items: r.data.items || [] });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load CVs");
      setState({ loading: false, locked: false, items: [] });
    }
  };
  useEffect(() => { load(); }, []);

  const download = async (cvId, fileType) => {
    try {
      const res = await axios.get(`${API}/user/cvs/${cvId}/download/${fileType}`, {
        responseType: "blob",
        withCredentials: true,
      });
      const contentType = res.headers["content-type"] || "application/octet-stream";
      const cd = res.headers["content-disposition"] || "";
      const nameMatch = cd.match(/filename="?([^"]+)"?/);
      const filename = nameMatch ? nameMatch[1] : `${fileType}.bin`;
      const url = URL.createObjectURL(new Blob([res.data], { type: contentType }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Download failed");
    }
  };

  const remove = async (cvId) => {
    if (!window.confirm(t.delete_confirm)) return;
    try {
      await axios.delete(`${API}/user/cvs/${cvId}`, { withCredentials: true });
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="my-cvs-page">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-[#78716C] hover:text-[#D95D39] transition-colors mb-6"
        data-testid="my-cvs-back"
      >
        <ArrowLeft className="w-4 h-4" /> {t.back}
      </button>

      <div className="mb-8">
        <div className="overline mb-2">{t.sub}</div>
        <h1 className="font-heading font-extrabold text-4xl lg:text-5xl text-[#292524] tracking-tight">
          {t.title}
        </h1>
      </div>

      {error && (
        <div className="mb-4 text-sm text-[#D9423E] bg-[#FDEEEC] border border-[#D9423E]/30 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {state.loading ? (
        <div className="text-[#78716C] flex items-center gap-2 py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> {t.loading}
        </div>
      ) : state.locked ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden bg-gradient-to-br from-[#292524] via-[#3d3730] to-[#2A5C5A] text-white rounded-3xl p-8 lg:p-12"
          data-testid="my-cvs-locked"
        >
          {/* Decorative elements */}
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#E5A93D]/20 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#D95D39]/20 blur-3xl" />

          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center mb-5">
              <Lock className="w-7 h-7 text-[#E5A93D]" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E5A93D] text-[#292524] text-[11px] font-bold mb-4">
              <Crown className="w-3 h-3" /> PREMIUM
            </div>
            <h2 className="font-heading font-extrabold text-3xl lg:text-4xl mb-3">{t.locked_title}</h2>
            <p className="text-white/80 text-lg max-w-lg leading-relaxed mb-6">{t.locked_sub}</p>
            <button
              onClick={onOpenRedeem}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#D95D39] hover:bg-[#C24F2E] text-white font-bold rounded-xl transition-colors shadow-lg"
              data-testid="my-cvs-locked-cta"
            >
              <Ticket className="w-4 h-4" /> {t.locked_cta}
            </button>
          </div>
        </motion.div>
      ) : state.items.length === 0 ? (
        <div className="bg-white border border-[#EAE5DB] rounded-3xl p-10 text-center" data-testid="my-cvs-empty">
          <div className="w-16 h-16 rounded-2xl bg-[#D95D39]/10 mx-auto mb-4 flex items-center justify-center">
            <FileText className="w-7 h-7 text-[#D95D39]" />
          </div>
          <div className="font-heading font-bold text-xl text-[#292524] mb-2">{t.empty_title}</div>
          <p className="text-[#78716C] mb-6">{t.empty_sub}</p>
          <button onClick={onStartWizard} className="btn-primary" data-testid="my-cvs-empty-cta">
            <Sparkles className="w-4 h-4" /> {t.empty_cta}
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="my-cvs-list">
          {state.items.map((cv, idx) => {
            const avail = cv.available_files || {};
            return (
              <motion.div
                key={cv.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white border border-[#EAE5DB] rounded-2xl p-5 hover:shadow-md transition-shadow"
                data-testid={`my-cvs-item-${cv.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="tag tag-brand text-[10px]">{cv.language?.toUpperCase() || "EN"}</span>
                      {cv.sector && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F2EBE1] text-[#57534E] text-[10px] font-semibold uppercase">
                          <Briefcase className="w-3 h-3" /> {cv.sector}
                        </span>
                      )}
                      {cv.visa_status && (
                        <span className="text-[10px] text-[#78716C] font-semibold uppercase">visa {cv.visa_status}</span>
                      )}
                    </div>
                    <div className="font-heading font-bold text-lg text-[#292524] truncate">
                      {cv.candidate_name || "Candidate"}
                    </div>
                    {cv.job_preview && (
                      <div className="text-sm text-[#57534E] mt-1 truncate" title={cv.job_preview}>
                        <span className="text-[#78716C]">{t.for_job}:</span> {cv.job_preview}
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-[#78716C]">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {formatDate(cv.created_at, lang)}
                      </span>
                      {cv.job_location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {cv.job_location}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <div className="overline text-[10px]">{t.ats}</div>
                      <div className="font-heading font-extrabold text-lg text-[#292524]">
                        <span className="text-[#78716C]">{cv.score_before}</span>
                        <span className="text-[#D95D39] mx-1">→</span>
                        <span className="text-[#3B8268]">{cv.score_after}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => remove(cv.id)}
                      className="w-9 h-9 rounded-lg bg-[#F2EBE1] hover:bg-[#FDEEEC] text-[#78716C] hover:text-[#D9423E] flex items-center justify-center transition-colors"
                      title="Delete"
                      data-testid={`my-cvs-delete-${cv.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 pt-3 border-t border-[#F2EBE1]">
                  {["cv_pdf", "cv_docx", "cover_pdf", "action_plan_pdf"].map((ft) => {
                    const Icon = FILE_ICONS[ft];
                    const disabled = !avail[ft];
                    return (
                      <button
                        key={ft}
                        onClick={() => !disabled && download(cv.id, ft)}
                        disabled={disabled}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#FAF6F0] hover:bg-[#F2EBE1] border border-[#EAE5DB] text-[#292524] text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`my-cvs-download-${cv.id}-${ft}`}
                      >
                        <Icon className="w-3.5 h-3.5 text-[#D95D39] flex-shrink-0" />
                        <span className="truncate">{t[FILE_LABELS_KEY[ft]]}</span>
                        <Download className="w-3 h-3 ml-auto flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </main>
  );
}
